import { Component, OnDestroy, computed, effect, inject, untracked } from '@angular/core';
import { ChatIdentityService } from '../../core/chat-identity.service';
import { SurveyChatService } from '../../core/survey-chat.service';
import { ChatPanelComponent } from './chat-panel.component';

/**
 * How often duty is re-read while the widget is mounted.
 *
 * Nothing else polls it: `refreshDuty()` fires from `startBreak` and `endBreak`
 * and nowhere else, and the break countdown is a computed over `duty()` while
 * `Date.now()` is not a signal. Without this the panel reads "on break, back in
 * 15 min" for the rest of the day, long after the server has decided
 * `meOnBreak: false`. A minute is the resolution the countdown is drawn at, so
 * anything finer would be requests nobody can see the result of.
 *
 * It lives on the bubble rather than the panel or the break control because a
 * break has to end whether or not the panel is open — an unattended break
 * silences the pool — and because a per-second timer inside a leaf component is
 * a leak waiting to happen.
 */
const DUTY_POLL_MS = 60_000;

/**
 * The fixed bubble, its badge, and the only mount point chat has. It is the one
 * component that holds `SurveyChatService` for its lifecycle: `init()` once the
 * data-project session exists, `dispose()` on sign-out or teardown, and the duty
 * poll in between.
 *
 * It is mounted in `app.html`, at the root and outside the router, so unlike the
 * CRM and portal bubbles — which live inside the post-login layout — this one is
 * already on screen before anyone has signed in and is never re-created
 * afterwards. `init()` needs the data-project user id, which Supabase delivers
 * asynchronously, so a single call from `ngOnInit` runs too early and nothing
 * would ever call it again: the widget stayed invisible for the whole session.
 * The identity signal is therefore what drives start-up, not the lifecycle hook.
 *
 * Nothing renders while `available()` is false. That is the 503 case — the
 * feature toggle being off — and the login page, and an environment with no
 * `CHAT_API_URL`: all three are "there is no chat here", never an error message.
 *
 * Every class is prefixed `afs-chat-` and lives in this component's own inline
 * styles, so NobleUI's demo `.chat-*` set cannot reach it.
 */
@Component({
  selector: 'app-chat-bubble',
  standalone: true,
  imports: [ChatPanelComponent],
  styles: [`
    .afs-chat-bubble {
      position: fixed; right: 1.25rem; bottom: 1.25rem; z-index: 1040;
      display: grid; place-items: center; width: 52px; height: 52px;
      border: 0; border-radius: 50%; background: var(--bs-primary, #da272a); color: #fff;
      box-shadow: 0 6px 18px rgba(0,0,0,.25); cursor: pointer;
    }
    .afs-chat-bubble__badge {
      position: absolute; top: -2px; right: -2px; min-width: 20px; padding: 0 5px;
      border-radius: 10px; background: #fff; color: #b51f22;
      font-size: .7rem; font-weight: 700; line-height: 20px;
    }
    /* Clear of the PWA install and update banners, which also sit bottom-right. */
    @media (pointer: coarse) { .afs-chat-bubble { bottom: 5rem; } }
  `],
  template: `
    @if (chat.available()) {
      @if (chat.panelOpen()) {
        <app-chat-panel (dismiss)="toggle()" />
      }

      <button
        class="afs-chat-bubble"
        type="button"
        [attr.aria-label]="label()"
        [attr.aria-expanded]="chat.panelOpen()"
        (click)="toggle()"
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor"
             stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>
        </svg>

        @if (chat.badge().total > 0) {
          <span class="afs-chat-bubble__badge">{{ display() }}</span>
        }
      </button>
    }
  `,
})
export class ChatBubbleComponent implements OnDestroy {
  readonly chat = inject(SurveyChatService);
  private readonly identity = inject(ChatIdentityService);

  private dutyPoll: ReturnType<typeof setInterval> | null = null;

  readonly label = computed(() => {
    const total = this.chat.badge().total;
    return total === 0 ? 'Chat' : `Chat, ${total} unread`;
  });

  readonly display = computed(() => {
    const total = this.chat.badge().total;
    return total > 9 ? '9+' : String(total);
  });

  constructor() {
    // Signing in starts chat; signing out tears it down. Without the second half
    // the bubble would outlive the session and sit on the login page holding a
    // token that no longer works.
    effect(() => {
      if (this.identity.userId() !== null) {
        void this.chat.init();
        return;
      }
      // untracked: available() flips to true inside init(), and a dependency on
      // it here would re-run this effect for no reason.
      if (untracked(() => this.chat.available())) void this.chat.dispose();
    });

    this.dutyPoll = setInterval(() => {
      // Chat being switched off is a 503 the API has already given once.
      // Asking again every minute for the life of the tab helps nobody.
      if (!this.chat.available()) return;
      void this.chat.refreshDuty();
    }, DUTY_POLL_MS);
  }

  ngOnDestroy(): void {
    if (this.dutyPoll !== null) clearInterval(this.dutyPoll);
    this.dutyPoll = null;
    void this.chat.dispose();
  }

  async toggle(): Promise<void> {
    if (this.chat.panelOpen()) {
      this.chat.closePanel();
      return;
    }
    await this.chat.openPanel();
  }
}
